import { getListingMember } from 'app/lib/guardian';
import { TrustCircleListingBanner } from './trust-circle-listing-banner';
import { UserAvatarBadge } from 'app/components/user-avatar-badge';
import { GuardianBadge } from 'app/shopify/components/feedback/GuardianBadge';
import { useListingCategoriesEnabled } from 'app/hooks/use-listing-categories';
import type { CategoryIcons } from 'app/design/categories/tab';
import { CategoryTab } from 'app/design/categories/tab';
import type { Category } from 'app/types/category';
import { getI18nLanguage } from 'app/lib/language';
import { useListingCatalog } from 'app/hooks/use-listing-catalog';
import { Dot } from 'app/design/icons/dot';
import { GeoPin } from 'app/design/icons/geo-pin';
import { NumFavs } from 'app/design/icons/num-favs';
import { NumViews } from 'app/design/icons/num-views';
import { Column, Row } from 'app/design/primitives/box';
import { PCImage } from 'app/design/primitives/image';
import { Text } from 'app/design/primitives/text';

import { useListingCard } from 'app/hooks/use-listing-card';

import { getImageVariantUrl } from 'app/lib/image-variants';
import { getListingCatalogLabels } from 'app/lib/listing-catalog-labels';
import { localeAwareFormatDurationShort } from 'app/lib/date';

import { useUser } from 'app/network/user';
import { Placeholder, PlaceholderLine, PlaceholderMedia, ShineOverlay } from 'app/overrides/rn-placeholder';
import type { Listing, ListingType, ListingSubtype } from 'app/types/listing';
import { objectsAreEquivalent, ICON_STROKE_WIDTH } from 'app/utils/constants';
import { useUISize } from 'app/utils/web-helpers';
import React, { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ColorValue } from 'react-native';
import { Pressable, StyleSheet, View } from 'react-native';
import { type ViewProps } from 'react-native-svg/lib/typescript/fabric/utils';
import { CorpoImage } from './corp-image';
import { FavouriteButton } from './favourite-button';
import type { ListingOfferCardProps } from 'app/types/listing-offer-card-props';
import { ListingThumbnail } from './listing-thumbnail';
import { ListingCardHorizontalMenu } from './listing-card-horizontal-menu';
import { useDistanceUnit } from 'app/hooks/use-distance-unit';
import { useMappingHelper } from '@shopify/flash-list';
import { Checkbox, createTheme as createDSTheme } from 'app/shopify';
import { IconEyeOff } from '@tabler/icons-react-native';
import {
  ListingCardModeBadge,
  ListingCardPerishableIcon,
  ListingCardPrice,
  ListingCardBadges,
  isListingNearby,
} from './listing-card-metadata';
const theme = createDSTheme();
export const OFFER_CARD_HEIGHT = 200;
export const OFFER_CARD_WIDTH = 200;
const MANAGEMENT_OFFER_CARD_SIZE = 220;
const MENU_TRIGGER_SIZE = 46;

const ListingOfferCardHorizontal = memo(function ListingOfferCardHorizontalComponent(props: ListingOfferCardProps) {
  const {
    continueTutorial,
    hideCorpoBadge = false,
    isFirst,
    isNew,
    isSelecting,
    listing,
    onBlockDates,
    onChangeLoanFee,
    onDelete,
    onHide,
    onUnhide,
    selectedRowsState,
    setListingForMenuAction,
    tutorialViewRef: firstOfferTutorialViewRef,
    horizontalVariant = 'default',
  } = props;
  const { t } = useTranslation('all-listings');
  const { isSmallUI } = useUISize();
  const [menuOpen, setMenuOpen] = useState(false);

  const { id, type, subtype, before, groups } = listing;

  const {
    isAuthor,
    isInvisible,
    tutorialViewRef,
    isSelected,
    displayTrustPoints,
    onPress,
    loanFeeAvailable,
    displayNumFavourites,
    displayNumViews,
    thumbPicture,
  } = useListingCard({
    continueTutorial,
    isFirst,
    isSelecting,
    listing,
    selectedRowsState,
    tutorialViewRef: firstOfferTutorialViewRef,
  });

  const layout = getHorizontalOfferLayout(horizontalVariant, isSmallUI);
  const { cardSize } = layout;
  const menuInset = (layout.padding ? theme.spacing[layout.padding] : 0) + layout.borderWidth;

  const showMenuOpener = [onBlockDates, onChangeLoanFee, onHide, onUnhide, onDelete].some(Boolean);
  return (
    <View style={{ position: 'relative', zIndex: menuOpen ? 1 : 0 }}>
      <Pressable
        accessibilityRole={isSelecting ? 'checkbox' : 'button'}
        accessibilityState={isSelecting ? { checked: Boolean(isSelected) } : undefined}
        onPress={onPress}
        ref={tutorialViewRef}
        style={{ pointerEvents: menuOpen ? 'none' : 'auto' }}
      >
        <Row
          alignItems={layout.alignItems}
          borderColor={layout.borderColor}
          borderRadius={layout.borderRadius}
          borderWidth={layout.borderWidth}
          gap={layout.gap}
          marginRight={layout.marginRight}
          padding={layout.padding}
          style={{ width: '100%' }}
        >
          {isSelecting ? (
            <View
              pointerEvents="none"
              style={{ height: cardSize, width: 24, flexDirection: 'row', alignItems: 'center' }}
            >
              <Checkbox selected={Boolean(isSelected)} showText={false} />
            </View>
          ) : null}

          <Row
            flex={layout.contentFlex}
            gap={layout.contentGap}
            style={{
              width: layout.contentWidth,
              borderWidth: layout.contentBorderWidth,
              borderColor: layout.contentBorderColor,
              borderRadius: theme.borderRadii['radius.lg'],
            }}
          >
            <View>
              <HorizontalOfferThumbnail
                isInvisible={isInvisible}
                before={before}
                listing={listing}
                thumbPicture={thumbPicture}
                subtype={subtype}
                type={type}
                cardSize={cardSize}
                isAuthor={isAuthor}
                isNew={isNew}
                groups={groups}
                hideCorpoBadge={hideCorpoBadge}
              />
            </View>

            <Column flex={1} justifyContent="space-between" paddingVertical={layout.detailsPadding}>
              <HorizontalOfferDetails
                listing={listing}
                isAuthor={isAuthor}
                layout={layout}
                displayNumViews={displayNumViews}
                displayNumFavourites={displayNumFavourites}
                loanFeeAvailable={loanFeeAvailable}
                t={t}
              />

              <Row gap="spacing.general.x2">
                <ListingPostedByMembers
                  avatarSize={loanFeeAvailable ? 24 : 30}
                  grouping={listing.grouping}
                  loanFee={listing.loanFee}
                  user={{ ...getListingMember(listing), trustPoints: displayTrustPoints }}
                />
              </Row>
            </Column>

            <Column justifyContent="center" paddingRight={layout.actionsPadding}>
              {!isAuthor ? <FavouriteButton listing={listing} /> : null}
            </Column>

            {showMenuOpener ? (
              <Pressable
                style={styles.moreBtn}
                onPress={() => {
                  setMenuOpen(!menuOpen);
                  setListingForMenuAction?.(id);
                }}
              >
                <Column alignItems="center" style={{ justifyContent: 'center', height: 30, width: 30, gap: 2 }}>
                  <Dot />
                  <Dot />
                  <Dot />
                </Column>
              </Pressable>
            ) : null}
          </Row>
        </Row>
      </Pressable>
      {showMenuOpener ? (
        <ListingCardHorizontalMenu
          isOpen={menuOpen}
          setIsMenuOpen={setMenuOpen}
          onBlockDates={onBlockDates}
          onChangeLoanFee={onChangeLoanFee}
          onHide={onHide}
          onUnhide={onUnhide}
          onDelete={onDelete}
          style={{
            top: menuInset + MENU_TRIGGER_SIZE,
            right: menuInset + (layout.marginRight ? theme.spacing[layout.marginRight] : 0),
          }}
        />
      ) : null}
    </View>
  );
}, objectsAreEquivalent);

function HorizontalOfferCatalogLabels({ listing }: Pick<ListingOfferCardProps, 'listing'>) {
  const { featureFlags, tagsEnabled } = useListingCatalog();
  const catalogLabels = tagsEnabled ? getListingCatalogLabels(listing, featureFlags) : [];
  return catalogLabels.length > 0 ? (
    <Row alignItems="center" gap="spacing.general.x1">
      <Row
        alignItems="center"
        backgroundColor="surface.tertiary-neutral"
        borderRadius="radius.xl"
        flexShrink={1}
        padding="spacing.general.x1"
      >
        <Text
          color="text.secondary"
          flexShrink={1}
          fontSize={12}
          fontWeight="600"
          numberOfLines={1}
          paddingHorizontal="spacing.general.x1"
        >
          {catalogLabels[0]?.label}
        </Text>
      </Row>
      {catalogLabels.length > 1 ? (
        <Text color="text.secondary" fontSize={12}>
          +{catalogLabels.length - 1}
        </Text>
      ) : null}
    </Row>
  ) : null;
}

function HorizontalOfferDetails({
  listing,
  isAuthor,
  layout,
  displayNumViews,
  displayNumFavourites,
  loanFeeAvailable,
  t,
}: {
  listing: Listing;
  isAuthor: boolean;
  layout: ReturnType<typeof getHorizontalOfferLayout>;
  displayNumViews: ReturnType<typeof useListingCard>['displayNumViews'];
  displayNumFavourites: ReturnType<typeof useListingCard>['displayNumFavourites'];
  loanFeeAvailable: boolean;
  t: ReturnType<typeof useTranslation<'all-listings'>>['t'];
}) {
  const i18nLanguage = getI18nLanguage();
  const categoriesEnabled = useListingCategoriesEnabled();
  const { getMappingKey } = useMappingHelper();
  const { formatDistanceWithoutText } = useDistanceUnit();
  const { categoryPrimary, item, distance } = listing;
  const categoryTabTextColor = categoryPrimary.colorDark;
  const { useManagementLayout, useCompactManagementLayout } = layout;

  return (
    <Column gap="spacing.general.x1">
      <Row alignItems="center" gap="spacing.general.x2">
        <Text
          flexShrink={1}
          fontSize={layout.fontSize}
          fontWeight="700"
          lineHeight={layout.lineHeight}
          numberOfLines={2}
          variant="bodyLarge"
        >
          {item.name}
        </Text>
      </Row>

      {!isAuthor && (
        <Column gap="spacing.general.x1">
          {distance !== undefined ? (
            <Row alignItems="center" gap="spacing.general.x1">
              <ListingCardPerishableIcon listing={listing} />
              <GeoPin color={theme.colors['icon.secondary']} width={18} />
              <Text paddingLeft="spacing.general.x0,5">{formatDistanceWithoutText(distance)}</Text>
            </Row>
          ) : null}

          {(categoriesEnabled ? [categoryPrimary] : [])
            .filter((x): x is Category => Boolean(x))
            .map((category, index) => (
              <Row flex={1} key={getMappingKey(category.id, index)}>
                <Row
                  alignItems="center"
                  borderRadius="radius.xl"
                  padding="spacing.general.x1"
                  style={{ backgroundColor: categoryPrimary.colorLight }}
                >
                  <CategoryTab category={category.icon as CategoryIcons} color={categoryTabTextColor} size={16} />
                  <Text
                    flexShrink={1}
                    fontSize={12}
                    fontWeight="600"
                    numberOfLines={1}
                    paddingHorizontal="spacing.general.x1"
                    style={{ color: categoryTabTextColor }}
                  >
                    {category.name[i18nLanguage]}
                  </Text>
                </Row>
              </Row>
            ))}
          {!categoriesEnabled && <HorizontalOfferCatalogLabels listing={listing} />}
          <ListingCardPrice listing={listing} />
        </Column>
      )}

      <HorizontalOfferOwnerStats
        isAuthor={isAuthor}
        displayNumViews={displayNumViews}
        displayNumFavourites={displayNumFavourites}
        useCompactManagementLayout={useCompactManagementLayout}
        useManagementLayout={useManagementLayout}
        categoryPrimary={categoryPrimary}
        categoryTabTextColor={categoryTabTextColor}
        t={t}
      />

      {loanFeeAvailable && !isAuthor && listing.loanFee ? <LoanFeeTag loanFee={listing.loanFee} /> : null}
    </Column>
  );
}

function getHorizontalOfferLayout(horizontalVariant: ListingOfferCardProps['horizontalVariant'], isSmallUI: boolean) {
  const useManagementLayout = horizontalVariant === 'management';
  const useCompactManagementLayout = useManagementLayout && isSmallUI;
  return {
    useManagementLayout,
    useCompactManagementLayout,
    cardSize: useCompactManagementLayout
      ? Number(theme.tokens.sizing['sizing.general.x30'])
      : useManagementLayout
        ? MANAGEMENT_OFFER_CARD_SIZE
        : OFFER_CARD_HEIGHT,
    alignItems: useManagementLayout ? 'center' : undefined,
    borderColor: useManagementLayout ? 'border.divider' : undefined,
    borderRadius: useManagementLayout ? 'radius.lg' : undefined,
    borderWidth: useManagementLayout ? 1 : 0,
    gap: useCompactManagementLayout
      ? 'spacing.general.x3'
      : useManagementLayout
        ? 'spacing.general.x4'
        : 'spacing.general.x10',
    marginRight: useManagementLayout ? undefined : 'spacing.general.x2',
    padding: useCompactManagementLayout ? 'spacing.general.x3' : useManagementLayout ? 'spacing.general.x4' : undefined,
    contentFlex: useManagementLayout ? 1 : undefined,
    contentGap: useCompactManagementLayout ? 'spacing.general.x3' : 'spacing.general.x4',
    contentWidth: useManagementLayout ? undefined : '100%',
    contentBorderWidth: useManagementLayout ? 0 : 1,
    contentBorderColor: useManagementLayout ? undefined : theme.colors['surface.inverse.tertiary-neutral'],
    detailsPadding: useCompactManagementLayout ? 'spacing.general.x2' : 'spacing.general.x4',
    fontSize: useCompactManagementLayout ? 16 : useManagementLayout ? 20 : undefined,
    lineHeight: useCompactManagementLayout ? 20 : useManagementLayout ? 24 : undefined,
    actionsPadding: useCompactManagementLayout ? undefined : 'spacing.general.x4',
  } as const;
}
function HorizontalOfferOwnerStats({
  isAuthor,
  displayNumViews,
  displayNumFavourites,
  useCompactManagementLayout,
  useManagementLayout,
  categoryPrimary,
  categoryTabTextColor,
  t,
}: {
  isAuthor: boolean;
  displayNumViews: number | undefined;
  displayNumFavourites: number | undefined;
  useCompactManagementLayout: boolean;
  useManagementLayout: boolean;
  categoryPrimary: import('app/types/listing').Listing['categoryPrimary'];
  categoryTabTextColor: string;
  t: ReturnType<typeof useTranslation<'all-listings'>>['t'];
}): React.ReactNode {
  const fontSize = useCompactManagementLayout ? 16 : useManagementLayout ? 20 : 16;
  const lineHeight = useCompactManagementLayout ? 20 : useManagementLayout ? 24 : undefined;
  if (!isAuthor) {
    return null;
  }
  if (Number.isInteger(displayNumViews) && Number.isInteger(displayNumFavourites)) {
    return (
      <Row alignItems="center" gap="spacing.general.x2" paddingVertical="spacing.general.x0,5">
        <Row alignItems="center">
          <NumViews />
          <Text fontSize={fontSize} lineHeight={lineHeight} paddingLeft="spacing.general.x0,5" variant="bodyBase">
            {displayNumViews}
          </Text>
        </Row>
        <Row alignItems="center">
          <NumFavs />
          <Text fontSize={fontSize} lineHeight={lineHeight} paddingLeft="spacing.general.x0,5" variant="bodyBase">
            {displayNumFavourites}
          </Text>
        </Row>
      </Row>
    );
  }
  return (
    <Row alignItems="center" paddingVertical="spacing.general.x0,5">
      <Row
        alignItems="center"
        borderRadius="radius.xl"
        paddingHorizontal="spacing.general.x1"
        paddingVertical="spacing.general.x1"
        style={{ backgroundColor: categoryPrimary.colorLight }}
      >
        <Text
          fontSize={12}
          fontWeight="600"
          paddingHorizontal="spacing.general.x1"
          style={{ color: categoryTabTextColor }}
        >
          {t('manageOffer', { ns: 'common' })}
        </Text>
      </Row>
    </Row>
  );
}

function HorizontalOfferThumbnail({
  isInvisible,
  before,
  listing,
  thumbPicture,
  subtype,
  type,
  cardSize,
  isAuthor,
  isNew,
  groups,
  hideCorpoBadge,
}: {
  isInvisible: boolean;
  before: string | null | undefined;
  listing: import('app/types/listing').Listing;
  thumbPicture: string | undefined;
  subtype: ListingSubtype;
  type: ListingType;
  cardSize: number;
  isAuthor: boolean;
  isNew: boolean | undefined;
  groups: import('app/types/listing').Listing['groups'];
  hideCorpoBadge: boolean;
}) {
  return (
    <Column width={cardSize} borderRadius="radius.md" overflow="hidden">
      <View>
        <View style={{ opacity: isInvisible ? 0.3 : 1 }}>
          <ListingThumbnail
            style={listing.isTrustCircleOnly ? { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 } : undefined}
            before={before}
            isFound={!!listing.lender}
            picture={thumbPicture}
            skipDonationOverlay
            subtype={subtype}
            type={type}
            size={cardSize}
          />
        </View>
        {isInvisible ? (
          <View pointerEvents="none" style={[StyleSheet.absoluteFill, styles.hiddenListingOverlay]}>
            <IconEyeOff strokeWidth={ICON_STROKE_WIDTH} size={44} />
          </View>
        ) : null}
        {!isAuthor ? <ListingCardBadges isNew={isNew} listing={listing} topInset={10} rightInset={10} /> : null}

        <Column
          position="absolute"
          bottom={!isAuthor && isListingNearby(listing.distance) ? 42 : 10}
          left={14}
          alignItems="flex-start"
          gap="spacing.general.x1"
        >
          {listing.guardianId ? <GuardianBadge /> : null}
          {Boolean(groups?.length) && !hideCorpoBadge && !listing.isTrustCircleOnly ? (
            <Row alignItems="center" style={{ backgroundColor: 'rgba(255,255,255,0.60)' }} borderRadius="radius.lg">
              <CorpoImage picture={groups?.[0]?.picture} />
            </Row>
          ) : null}
        </Column>
        <Row position="absolute" bottom={!isAuthor && isListingNearby(listing.distance) ? 42 : 10} right={10}>
          <ListingCardModeBadge listing={listing} />
        </Row>
      </View>
      <TrustCircleListingBanner listing={listing} />
    </Column>
  );
}

const styles = StyleSheet.create({
  hiddenListingOverlay: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  moreBtn: {
    alignSelf: 'flex-start',
    padding: 8,
    width: MENU_TRIGGER_SIZE,
    height: MENU_TRIGGER_SIZE,
  },
});

const ListingCardSkeleton = memo(function ListingCardSkeletonComponent() {
  const leftView = useCallback(
    (props: ViewProps) => (
      <PlaceholderMedia
        style={[
          {
            height: OFFER_CARD_HEIGHT,
            width: OFFER_CARD_WIDTH,
            borderRadius: theme.borderRadii['radius.md'],
          },
          props.style,
        ]}
      />
    ),
    [],
  );
  return (
    <Placeholder Animation={ShineOverlay} Left={leftView}>
      <PlaceholderLine style={{ width: 140, marginBottom: 20 }} />
      <PlaceholderLine style={{ width: 50, marginBottom: 40 }} />

      <PlaceholderLine style={{ width: 120, height: 30, borderRadius: theme.borderRadii['radius.md'] }} />
    </Placeholder>
  );
}, Boolean);

const LoanFeeTag = memo(function LoanFeeTagComponent({ loanFee }: { loanFee?: number | null }) {
  const { t } = useTranslation('all-listings');

  return loanFee ? (
    <Row
      alignItems="center"
      alignSelf="flex-start"
      backgroundColor="surface.inverse.tertiary-neutral"
      borderRadius="radius.lg"
      gap="spacing.general.x2"
      paddingHorizontal="spacing.general.x2"
      paddingVertical="spacing.general.x1"
    >
      <Text fontSize={12} variant="bodyBase">
        {t('x$', { ns: 'common', amount: loanFee.toFixed(loanFee % 1 === 0 ? 0 : 2) })}
      </Text>
      <Text fontSize={12} variant="bodyBase">
        {t('listingCard.loanFee', { ns: 'loanFee' })}
      </Text>
    </Row>
  ) : null;
});

const ListingPostedByMembers = memo(function ListingPostedByMembersComponent({
  avatarSize,
  ignoreIsAuthor,
  textColor,
  grouping,
  user,
  loanFee,
}: Pick<Listing, 'grouping' | 'loanFee'> & {
  avatarSize: number;
  user: Listing['user'];
  ignoreIsAuthor?: boolean;
  textColor?: ColorValue | string;
}) {
  const { t } = useTranslation('all-listings');
  const { data: myUser } = useUser();
  const { getMappingKey } = useMappingHelper();

  const isAuthor = myUser?.id === user.id;
  const isGroupedListing = Boolean(!isAuthor && grouping && grouping.totalOffers > 1);

  const maxNumberOfAvatars = 4;
  const metaFontSize = 12;

  return isGroupedListing ? (
    <Row alignItems="center" gap="spacing.general.x2">
      {grouping!.listings.slice(0, maxNumberOfAvatars).map((gListing: Listing, index: number) =>
        index === maxNumberOfAvatars - 1 && grouping!.totalOffers > maxNumberOfAvatars ? (
          <View
            key={getMappingKey('grouping-overflow', index)}
            style={{
              marginLeft: -((avatarSize * 18) / 30),
              borderColor: 'white',
              borderWidth: 1,
              borderRadius: avatarSize / 2,
              backgroundColor: theme.colors['text.brand'],
              width: avatarSize,
              height: avatarSize,
              alignItems: 'center',
            }}
          >
            <Text style={{ fontSize: (avatarSize * 11) / 30, lineHeight: avatarSize - 2, color: textColor }}>
              +{grouping!.totalOffers - maxNumberOfAvatars + 1}
            </Text>
          </View>
        ) : (
          <Row key={getMappingKey(gListing.id, index)} style={{ zIndex: maxNumberOfAvatars - index }}>
            <PCImage
              aspectRatio={1}
              cachePolicy="memory-disk"
              height={avatarSize}
              recyclingKey={`${getListingMember(gListing).profile.picture!}-card`}
              source={{
                uri: getImageVariantUrl(getListingMember(gListing).profile.picture, 'mini'),
              }}
              style={{
                marginLeft: index === 0 ? 0 : -18,
                borderColor: 'white',
                borderWidth: 1,
                borderRadius: theme.borderRadii['radius.xl'],
              }}
            />
            <UserAvatarBadge
              userId={getListingMember(gListing).id}
              avatarSize={avatarSize}
              isAmbassador={getListingMember(gListing).isAmbassador}
              isSuper={getListingMember(gListing).isSuper}
            />
          </Row>
        ),
      )}
      <Text
        ellipsizeMode="tail"
        flexShrink={1}
        numberOfLines={1}
        style={{ lineHeight: avatarSize, marginLeft: avatarSize < 30 ? -4 : 0, color: textColor }}
        variant="bodySmall"
      >
        {t('groupingMembers', { count: grouping!.totalOffers })}
      </Text>
    </Row>
  ) : isAuthor && !ignoreIsAuthor ? (
    <LoanFeeTag loanFee={loanFee} />
  ) : (
    <ListingMemberIdentity
      user={user}
      avatarSize={avatarSize}
      textColor={textColor}
      isAuthor={isAuthor}
      metaFontSize={metaFontSize}
      t={t}
    />
  );
});

export { ListingOfferCardHorizontal, ListingCardSkeleton, ListingPostedByMembers };

function ListingMemberIdentity({
  user,
  avatarSize,
  textColor,
  isAuthor,
  metaFontSize,
  t,
}: {
  user: Listing['user'];
  avatarSize: number;
  textColor: ColorValue | undefined;
  isAuthor: boolean;
  metaFontSize: number;
  t: ReturnType<typeof useTranslation<'all-listings'>>['t'];
}): React.ReactNode | Promise<React.ReactNode> {
  return (
    <Row alignItems="center">
      {user.profile.picture ? (
        <Row>
          <PCImage
            aspectRatio={1}
            cachePolicy="memory-disk"
            height={avatarSize}
            recyclingKey={`${user.profile.picture}-card`}
            source={{
              uri: getImageVariantUrl(user.profile.picture, 'mini'),
            }}
            style={{ borderRadius: theme.borderRadii['radius.xl'] }}
          />
          <UserAvatarBadge
            userId={user.id}
            avatarSize={avatarSize}
            isAmbassador={user.isAmbassador}
            isSuper={user.isSuper}
          />
        </Row>
      ) : null}

      <Column marginLeft={avatarSize < 30 ? 'spacing.general.x1' : 'spacing.general.x2'}>
        <Text flexShrink={1} numberOfLines={1} style={{ color: textColor, maxWidth: 180 }}>
          {user.profile.firstName} {user.profile.lastName}
        </Text>

        <ListingMemberActivity isAuthor={isAuthor} user={user} metaFontSize={metaFontSize} t={t} />
      </Column>
    </Row>
  );
}

function ListingMemberActivity({
  isAuthor,
  user,
  metaFontSize,
  t,
}: {
  isAuthor: boolean;
  user: Listing['user'];
  metaFontSize: number;
  t: ReturnType<typeof useTranslation<'all-listings'>>['t'];
}): React.ReactNode {
  return !isAuthor && (user.trustPoints || user.lastSeenAt) ? (
    <Text color="text.tertiary" fontSize={metaFontSize} numberOfLines={1} style={{ maxWidth: 180 }} variant="bodyBase">
      {user.trustPoints ? `👍 (${user.trustPoints.toFixed(0)})` : ''}
      {user.trustPoints && user.lastSeenAt ? ' | ' : ''}
      {user.lastSeenAt
        ? t('lastActiveAgoShort', {
            ago: localeAwareFormatDurationShort(new Date(user.lastSeenAt)),
            ns: 'common',
          })
        : ''}
    </Text>
  ) : null;
}
